import { format, parseISO } from 'date-fns';
import {
  Plan,
  Application,
  Applicant,
} from 'src/services/interfaces/application';
import { MONTH_MAP } from '@constants/index';
import { calculateCoverageLevel } from '@features/onboarding/utils/coverageLevel';
import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import { InsurancePlan } from 'src/services/interfaces/insurance';
import {
  getCriticalIllnessPriceId,
  PriceSearchConditions,
} from '@features/onboarding/utils/criticalIllnessPriceId';
import { useApplicationStore } from 'src/store/application';

export function transformApplicationData(
  data: Partial<OnboardingSchema>,
  plans: InsurancePlan[]
): Application {
  if (!data?.applicants?.find) {
    return { applicants: [], income: 0, address: {}, plans: [] };
  }

  const { acceptedTier, tier } = useApplicationStore.getState();
  const selectedPlanKeys = Object.keys(data).filter(
    (key) => key.endsWith('Plan') && !key.endsWith('PlansToCompare')
  );

  const income = data.householdIncome;

  const transformedApplicants = data.applicants.map(
    (applicant: OnboardingSchema['applicants'][0]) => ({
      applicant:
        applicant.applicant === 'primary'
          ? 1
          : applicant.applicant === 'spouse'
            ? 2
            : 3,
      firstName: applicant.firstName?.trim() || '',
      lastName: applicant.lastName?.trim() || '',
      midName: applicant.midName?.trim() || undefined,
      phone: undefined,
      gender: applicant.gender,
      dob: `${applicant?.dateOfBirth?.year ?? '1977'}-${MONTH_MAP[applicant?.dateOfBirth?.month] ?? '01'}-${applicant?.dateOfBirth?.day ?? '01'}`,
      nicotine: applicant.nicotine,
      weight: applicant.weight,
      heightFt: applicant.height.ft,
      heightIn: applicant.height.in,
    })
  );

  const getCoverageForPlan = (plan: InsurancePlan): number => {
    if (plan.category === 'Major Medical') {
      const tags = data.medicalTags || [];
      return calculateCoverageLevel(tags);
    }
    const planTags = (data.planTags || {}) as Record<string, string[]>;
    const tags = planTags[plan.uid] || [];
    return calculateCoverageLevel(tags);
  };

  const formPlan = selectedPlanKeys
    .map((planKey) => {
      const plan = plans?.find(
        (p) => p.uid === data[planKey as keyof typeof data]
      );
      if (!plan) return null;

      let selectedTier: string | undefined = acceptedTier?.toString() || undefined;
      let selectedCoverageAmount: number | undefined;

      if (plan.category === 'Major Medical') {
        if (data.medicalPlanTier) {
          selectedTier = data.medicalPlanTier;
        }
        if (data.medicalPlanCoverageAmount) {
          selectedCoverageAmount = data.medicalPlanCoverageAmount;
        }
      } else if (plan.category === 'Dental') {
        if (data.dentalPlanTier) {
          selectedTier = data.dentalPlanTier;
        }
        if (data.dentalPlanCoverageAmount) {
          selectedCoverageAmount = data.dentalPlanCoverageAmount;
        }
      } else if (plan.category === 'Critical Illness') {
        const criticalIllnessPlanKey = planKey.replace(
          'Plan',
          'PlanCoverageAmount'
        );
        const coverageAmount = (data as Record<string, unknown>)[
          criticalIllnessPlanKey
        ] as number | undefined;
        if (coverageAmount) {
          selectedCoverageAmount = coverageAmount;
        }
      }

      if (plan.category === 'Critical Illness' && !selectedCoverageAmount) {
        selectedCoverageAmount = 10000;
      }

      if (
        plan.category === 'Critical Illness' &&
        selectedCoverageAmount === undefined
      ) {
        selectedCoverageAmount = 10000;
      }

      let selectedApplicants: number[] = [];

      if (plan.category === 'Major Medical') {
        selectedApplicants = data.medicalTags?.map(e => +e.replace(/\D/g, '')) || [];
      } else {
        const planTags = data.planTags || {};
        selectedApplicants = plan?.uid && planTags[plan?.uid]?.map(e => +e.replace(/\D/g, '')) || [];
      }
      selectedApplicants = Array.from(new Set([0, ...selectedApplicants]));

      let primaryAge = 30;
      if (data.applicants && data.applicants.length > 0) {
        const primaryApplicant = data.applicants.find(
          (app) => app.applicant === 'primary'
        );
        if (primaryApplicant && primaryApplicant.dateOfBirth) {
          const birthYear = parseInt(primaryApplicant.dateOfBirth.year);
          const currentYear = parseISO(format(new Date(), 'yyyy-MM-dd')).getFullYear();
          primaryAge = currentYear - birthYear;
        }
      }

      let dependentsCount = 0;
      let hasSpouse = false;
      if (plan.category === 'Critical Illness') {
        const planTags = data.planTags || {};
        const planTagsForThisPlan = planTags[plan.uid] || [];
        dependentsCount = planTagsForThisPlan.filter((tag: string) =>
          tag.includes('dependent')
        ).length;
        hasSpouse = planTagsForThisPlan.some((tag: string) =>
          tag.includes('spouse')
        );
      }

      let priceId: number | undefined;

      if (plan.category === 'Critical Illness') {
        const conditions: PriceSearchConditions = {
          coverageAmount: selectedCoverageAmount || 10000,
          primaryAge,
          dependentsCount,
          hasSpouse,
        };

        priceId = getCriticalIllnessPriceId(plan, conditions);

        // Если priceId не найден, ищем fallback по coverage level
        if (!priceId && plan.prices && plan.prices.length > 0) {
          const fallbackPrice = plan.prices.find(
            (e) => e.coverage === getCoverageForPlan(plan)
          );
          priceId = fallbackPrice?.id || plan.prices[0].id;
          console.log(
            'fallback priceId:',
            priceId,
            'coverage level:',
            getCoverageForPlan(plan)
          );
        }
      } else {
        // Ищем цену по tier И coverage level
        const coverageLevel = getCoverageForPlan(plan);
        const defaultPlanPrice = plan.prices.find(
          (e) => e.tier === (acceptedTier?.toString() || selectedTier || tier) && e.coverage === coverageLevel
        );
        priceId = defaultPlanPrice?.id;

        if (!priceId) {
          console.log(
            'Available prices:',
            plan.prices.map((p) => ({
              id: p.id,
              tier: p.tier,
              coverage: p.coverage,
            }))
          );
        }
      }

      const finalPriceId =
        priceId ||
        (() => {
          // Fallback: ищем любую цену с нужным coverage level
          if (plan.prices && plan.prices.length > 0) {
            const coverageLevel = getCoverageForPlan(plan);
            const fallbackPrice = plan.prices.find(
              (e) => e.coverage === coverageLevel
            );
            return fallbackPrice?.id || plan.prices[0].id;
          }
          return 0;
        })();

      if (plan.category === 'Major Medical' && data.medicalPlanRejected) {
        return null
      }

      return {
        id: plan.id,
        priceId: finalPriceId,
        coverage: getCoverageForPlan(plan), // Добавляем правильный coverage level
        // tier: selectedTier || null,
        // coverageAmount:
        //   selectedCoverageAmount && selectedCoverageAmount > 0
        //     ? selectedCoverageAmount.toString()
        //     : '10000',
        applicants: selectedApplicants,
      } as Plan;
    })
    .filter((e): e is Plan => !!e);

  // Preserve existing address and SSN from current application or use from form data
  const existingApplication = useApplicationStore.getState().applications?.[0];
  const address = data.address || existingApplication?.address || {};
  const ssn = data.ssn || existingApplication?.ssn || null;

  const application = {
    applicants: transformedApplicants,
    income,
    address,
    plans: formPlan,
    ssn,
  } as Application | any;

  if (data.healthQuestions) {
    // Должно работать, но почему-то не работает 🤷‍♂️
    // const date = new Date(); // всегда хранит время в UTC

    // Форматирование в UTC:
    // const utcYear = date.getUTCFullYear();
    // const utcMonth = date.getUTCMonth() + 1;
    // const utcDay = date.getUTCDate();
    // const utcHours = date.getUTCHours();
    // const utcMinutes = date.getUTCMinutes();

    // const effectiveDate = `${utcYear}-${String(utcMonth).padStart(2, '0')}-${String(utcDay).padStart(2, '0')}T${String(utcHours).padStart(2, '0')}:${String(utcMinutes).padStart(2, '0')}:00Z`;

    // --- Normalize effectiveDate to UTC YYYY-MM-DD ---
    const rawEffectiveDate = data.healthQuestions?.coverageStartDate?.effectiveDate;
    let effectiveDate: string | undefined = undefined;
    if (rawEffectiveDate) {
      try {
        const parsed = parseISO(rawEffectiveDate.endsWith('Z') ? rawEffectiveDate : `${rawEffectiveDate}Z`);
        effectiveDate = parsed.toISOString().split('T')[0];
      } catch {
        effectiveDate = rawEffectiveDate; // fallback to raw if parsing fails
      }
    }

    const signature = data.healthQuestions?.privacyDisclosure?.signature;
    const rawMedications = (data.healthQuestions?.takingMedicationsNow as any)
      ?.medications;
    const medications = Array.isArray(rawMedications)
      ? rawMedications.map(
        (med: {
          applicant?: string;
          name: string;
          rxcui: string;
          dosage: string;
          frequency: string;
          frequencyDescription?: string;
          description?: string;
        }) => {
          let applicantNumber = 0;
          if (med.applicant) {
            if (med.applicant === '0' || med.applicant === 'primary') {
              applicantNumber = 0;
            } else if (med.applicant === '1' || med.applicant === 'spouse') {
              applicantNumber = 1;
            } else if (
              med.applicant === '2' ||
              med.applicant === 'dependent'
            ) {
              applicantNumber = 2;
            }
          }

          return {
            applicant: applicantNumber,
            name: med.name,
            rxcui: med.rxcui,
            dosage: med.dosage,
            frequency: med.frequency,
            frequencyDescription: med.frequencyDescription || '',
            description: med.description || '',
          };
        }
      )
      : [];

    const rawIssues = (
      data.healthQuestions?.surgeriesDiabetesHeartMental as any
    )?.conditions;
    const issueDetails =
      (data.healthQuestions?.surgeriesDiabetesHeartMental as any)?.details ||
      {};

    const issues = Array.isArray(rawIssues)
      ? rawIssues
        .filter((issue: any) => issue.value && issue.value !== 'none')
        .map((issue: any) => {
          const conditionDetails = issueDetails[issue.value] || '';

          return {
            key: issue.value,
            details: conditionDetails
              ? [
                {
                  key: issue.value,
                  description: conditionDetails,
                },
              ]
              : [],
          };
        })
        .filter((issue: any) => issue.key)
      : [];

    const rawOtherHealthConditions = (data.healthQuestions?.otherHealthConditions as any)
      ?.conditions;
    const otherHealthConditionDetails =
      (data.healthQuestions?.otherHealthConditions as any)?.details || {};

    const otherHealthConditions = Array.isArray(rawOtherHealthConditions)
      ? rawOtherHealthConditions
          .filter(
            (condition: any) => condition?.value && condition.value !== 'none'
          )
          .map((condition: any) => {
            const key = condition.value;
            const rawDescription = otherHealthConditionDetails?.[key];
            const description =
              typeof rawDescription === 'string' ? rawDescription.trim() : '';
            return {
              key,
              description,
            };
          })
          .filter((condition: { key: string }) => Boolean(condition.key))
      : [];

    application.phq = {
      treatment: data.healthQuestions?.initialQuestions?.treatment || false,
      invalid: data.healthQuestions?.initialQuestions?.invalid || false,
      pregnancy: data.healthQuestions?.initialQuestions?.pregnancy || false,
      effectiveDate,
      disclaimer: data.healthQuestions?.privacyDisclosure?.accepted || false,
      signature: signature || undefined,
      medications: medications,
      issues: issues,
      conditions: otherHealthConditions,
    };
  }

  return application;
}
